import { DataService } from '../../../services/data.service';
import { AfterViewInit, Component, HostListener, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AppStateService } from '../../../services/app-state.service';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import XLSX from 'xlsx';
import { ActivatedRoute, Router } from '@angular/router';
import { ACCOUNT_METRICS_CONFIG, APPLICATION_METRICS_CONFIG, Operator, USER_METRICS_CONFIG } from '../models/metrics-models';
import { ClientAccount, Dictionary } from 'src/app/services/app-state.model';
import { DialogFactoryService } from '@theevolvedgroup/component-library/dialog-base';
import { DialogCloseResult } from '@theevolvedgroup/component-library/dialog-base/types';
import { FieldSettingsModel } from '@syncfusion/ej2-dropdowns';

@Component({
  selector: 'app-metrics-report',
  templateUrl: './metrics-report.component.html',
  styleUrls: ['./metrics-report.component.less']
})
export class MetricsReportComponent implements OnInit, AfterViewInit {
  @ViewChild("alarmTemplate", { read: TemplateRef }) alarmTemplate: TemplateRef<any>;
  
  reportName: string;
  reportTitle: string;
  reportSubtitle: string;
  reportDataSource: any[] = [];
  reportColumns: any[] = [];

  showInactiveFilter: boolean = false;
  showInactiveUsers: boolean = false;

  showEmailFilter: boolean = true;
  externalUsersOnly: boolean = true;

  showAccountsFilter: boolean = false;
  selectedAccountGuids: string[] = [];

  headerHeight: number = 220;
  viewportHeight: number = window.innerHeight - this.headerHeight;

  accountFields: FieldSettingsModel = { text: "clientAccountName", value: "clientAccountGuid" };
  activeAccounts: ClientAccount[] = [];

  alarmColumnConfig: any = {
    field: "alarmStatus",
    headerText: "Alarm",
    width: "100px",
    type: "string",
    allowFiltering: true,
    allowSorting: true
}

  private loadReportDataDebounced: () => void;

  constructor(
    public appState: AppStateService,
    public dataService: DataService,
    public dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private dialogFactoryService: DialogFactoryService) {
    this.loadReportDataDebounced = _.debounce(this.loadReportData, 1000);

  }

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.viewportHeight = window.innerHeight - this.headerHeight;
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.reportName = params['report'];

      let allReports = [...ACCOUNT_METRICS_CONFIG, ...USER_METRICS_CONFIG, ...APPLICATION_METRICS_CONFIG];
      let validReport = _.find(allReports, { action: this.reportName });

      if (!validReport) {
        this.router.navigate(['/metrics']);
        return;
      }

      this.reportTitle = validReport.title;
      this.reportSubtitle = validReport.subtitle;

      if (!validReport.columns || validReport.columns.length < 1) {
        let headerText = "Report Unavailable";
        let message = "This report is unavailable. Only the report tiles with a green action bar are available at the moment.";
        let buttons = [{ id: "ok", colorType: "error", caption: "OK" }];
        let dialog = this.dialogFactoryService.openMessageDialog({ headerText, contentComponentProperties: { message }, buttons, headerColor: "error" });

        dialog.closed$.subscribe((result: DialogCloseResult) => {
          this.router.navigate(['/metrics']);
        });

        return;
      }
    });
  }

  ngAfterViewInit(): void {
    this.alarmColumnConfig.templateRef = this.alarmTemplate;
    this.activeAccounts = this.appState.clientAccounts.filter(x => !x.isDeleted && !x.isArchived);
    this.loadReportData();
  }

  loadReportData() {
    let allReports = [...ACCOUNT_METRICS_CONFIG, ...USER_METRICS_CONFIG, ...APPLICATION_METRICS_CONFIG];
    let currentReport = allReports.find(x => x.action == this.reportName);

    this.showEmailFilter = currentReport.showEmailFilter;
    this.showInactiveFilter = currentReport.showInactiveFilter;
    this.showAccountsFilter = currentReport.showAccountsFilter;

    let reportParameters: Dictionary = {
      "clientAccountGuid": this.appState.currentAccount?.clientAccountGuid
    };

    if (this.showInactiveFilter) {
      reportParameters["showInactiveUsers"] = this.showInactiveUsers;
    }

    if (this.showEmailFilter) {
      reportParameters["externalUsersOnly"] = this.externalUsersOnly;
    }

    if (this.showAccountsFilter) {
      this.selectedAccountGuids = JSON.parse(localStorage.getItem("selectedAccountGuids"));

      if (this.selectedAccountGuids && this.selectedAccountGuids.length > 0)
        reportParameters["clientAccountGuids"] = JSON.stringify(this.selectedAccountGuids);
      else {
        this.reportDataSource = [];
        return;
      }
    }

    this.appState.dataLoaded = false;
    this.dataService.getMetrics(this.reportName, reportParameters).subscribe((response: any) => {

      if (response.length < 1) {
        this.appState.dataLoaded = true;
        return;
      }

      this.reportColumns = _.filter(currentReport.columns, (c) => { return !c.hidden });

      // Alarm Configuration is defined in the Report Config. Use rules and determine alarms.
      if (currentReport.alarmConfig) {
        this.reportColumns.push(this.alarmColumnConfig);
      }
      // Alarms returned with report data. Push template column.
      else {
        let firstRow = _.first(response);
        let alarmStatus = firstRow["alarmStatus"];

        if (alarmStatus) {
          this.reportColumns.push(this.alarmColumnConfig);
        }
      }

      this.populateAlarms(response, currentReport);

      this.reportColumns = [...this.reportColumns];
      this.reportDataSource = [...response];
      this.appState.dataLoaded = true;
    });
  }

  populateAlarms(dataSource, reportConfig) {
    if (!dataSource || !reportConfig) {
        return;
    }

    // Iterate through each row of the report and check for alarms
    _.forEach(dataSource, (row) => {

      if(row.alarmStatus) {
        row.alarmSeverity = 1;
        row.alarmMessage = "The value for this metric has raised an alarm."
      }
      else {
        row.alarmStatus = "";

        _.forEach(reportConfig.alarmConfig, (alarm) => {
          let value = row[alarm.field];
          let alarmRaised = this.checkAlarmCondition(value, alarm.value, alarm.operator);
          
          if (alarmRaised) {
            let column = _.find(reportConfig.columns, { field: alarm.field });

            row.alarmStatus = "YES";
            row.alarmSeverity = alarm.severity ?? 1;
            row.alarmMessage = alarm.message ?? `${column.headerText} is ${value}.`;
            return false;
          }
        });
      }
    });

    dataSource = [...dataSource];
  }

  checkAlarmCondition(value: number, limit: number, operator: Operator) {

    if (value == null || value == undefined)
        return false;

    switch (operator) {
        case Operator.Equal:
            return value == limit;
        case Operator.LessThan:
            return value < limit;
        case Operator.LessThanOrEqual:
            return value < limit || value == limit;
        case Operator.GreaterThan:
            return value > limit;
        case Operator.GreaterThanOrEqual:
            return value > limit || value == limit;
        case Operator.NotEqual:
            return value != limit;
    }
}

  onInactiveUsersFilerChanged(value) {
    this.showInactiveUsers = value;
    this.loadReportDataDebounced();
  }

  onEmailFilterChanged(value) {
    this.externalUsersOnly = value;
    this.loadReportDataDebounced();
  }

  onDownloadClicked() {
    if (!this.reportDataSource || this.reportDataSource.length < 1 || !this.reportColumns || this.reportColumns.length < 1)
      return;

    var wb = XLSX.utils.book_new();
    let worksheetName = _.truncate(this.reportTitle.replace(/[\\\/:*?[\]]/g, ''));

    let worksheet = {
      name: worksheetName,
      label: worksheetName,
      header: [],
      records: [],
    };

    _.forEach(this.reportColumns, (c) => {
      worksheet.header.push(c.headerText);
    });

    _.forEach(this.reportDataSource, (r) => {
      let record = [];
      _.forEach(this.reportColumns, (c) => {
        record.push(r[c.field]);
      });
      worksheet.records.push(record);
    });

    var ws = XLSX.utils.aoa_to_sheet(_.concat([worksheet.header], worksheet.records));
    XLSX.utils.book_append_sheet(wb, ws, worksheet.label);

    var filename = `${this.reportTitle} Report [PROTECTED].xlsx`;
    XLSX.writeFile(wb, filename);
  }

  onAccountChanged(accounts) {
    if (!accounts)
      return;

    this.selectedAccountGuids = accounts;
    localStorage.setItem(
      'selectedAccountGuids',
      JSON.stringify(accounts)
    );
  }

  onApplyButtonClicked()
  { 
    this.loadReportData();
  }
}
